/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useContext } from "react";
import {
  Search,
  Filter,
  ChevronDown,
  RefreshCw,
  Calendar,
  User,
  AlertCircle,
} from "lucide-react";
import { AdminContext } from "../../../context/AdminContext";
import { toast } from "react-toastify";
import Pagination from "../../../components/Pagination";

const PayoutList = () => {
  const { fetchPayouts, payouts, retryPayout, loading } =
    useContext(AdminContext);

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [monthFilter, setMonthFilter] = useState("All");
  const [showFilters, setShowFilters] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);

  useEffect(() => {
    const filters = {};
    if (statusFilter !== "All") filters.status = statusFilter;
    if (monthFilter !== "All") filters.month = monthFilter;
    console.log("Fetching payouts with filters:", filters);
    fetchPayouts(filters);
  }, [statusFilter, monthFilter]);

  // Log payouts when they change
  useEffect(() => {
    console.log("Payouts state updated:", payouts);
  }, [payouts]);

  // Filter payouts
  const filteredPayouts = (payouts || []).filter((payout) => {
    const matchesSearch =
      payout.caregiver?.name
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      payout.contract?.client?.name
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  // Pagination
  const totalPages = Math.ceil(filteredPayouts.length / itemsPerPage);
  const paginatedPayouts = filteredPayouts.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, monthFilter, itemsPerPage]);

  const handleRetry = async (payoutId) => {
    await retryPayout(payoutId);
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case "SETTLED":
        return "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-400 dark:border-emerald-800";
      case "PENDING":
        return "bg-yellow-50 text-yellow-700 border-yellow-200 dark:bg-yellow-900/20 dark:text-yellow-400 dark:border-yellow-800";
      case "FAILED":
        return "bg-red-50 text-red-700 border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800";
      default:
        return "bg-gray-100 text-gray-700 border-gray-300";
    }
  };

  // Generate month options for the last 12 months
  const getMonthOptions = () => {
    const options = [{ value: "All", label: "All Months" }];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const value = date.toISOString().slice(0, 7); // YYYY-MM format
      const label = date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
      });
      options.push({ value, label });
    }
    return options;
  };

  return (
    <div className="animate-in fade-in slide-in-from-right-4 duration-500 pb-12">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-text dark:text-dark-text">
          Payout List
        </h1>
        <p className="text-gray-500 dark:text-dark-muted mt-1">
          View and manage all caregiver payouts
        </p>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-dark-surface p-4 rounded-xl shadow-sm border border-gray-200 dark:border-dark-border mb-6">
        <div className="flex flex-col md:flex-row gap-4 justify-between items-center mb-2">
          <div className="relative w-full md:flex-1">
            <Search
              className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
              size={20}
            />
            <input
              type="text"
              placeholder="Search by caregiver or client name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 rounded-lg bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-dark-border outline-none focus:ring-2 focus:ring-primary/20 text-text dark:text-dark-text placeholder-gray-400 font-medium transition-all"
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-6 py-3 rounded-lg font-semibold transition-all border ${
              showFilters
                ? "bg-primary/10 text-primary border-primary/20"
                : "bg-white text-gray-600 dark:bg-dark-surface dark:text-dark-muted border-gray-200 dark:border-dark-border hover:bg-gray-50 dark:hover:bg-gray-800"
            }`}
          >
            <Filter size={18} />
            <span>{showFilters ? "Hide Filters" : "Show Filters"}</span>
          </button>
        </div>

        {/* Collapsible Filters */}
        <div
          className={`grid grid-cols-1 md:grid-cols-2 gap-4 overflow-hidden transition-all duration-300 ease-in-out ${
            showFilters
              ? "max-h-40 opacity-100 mt-4 pt-4 border-t border-gray-200 dark:border-dark-border"
              : "max-h-0 opacity-0"
          }`}
        >
          <div className="space-y-1.5">
            <label className="text-xs font-bold text-gray-400 dark:text-dark-muted uppercase tracking-wider ml-1">
              Status
            </label>
            <div className="relative">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full appearance-none pl-4 pr-10 py-2.5 rounded-lg bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-dark-border text-sm font-medium text-text dark:text-dark-text focus:ring-2 focus:ring-primary/20 outline-none cursor-pointer"
              >
                <option value="All">All Statuses</option>
                <option value="SETTLED">Settled</option>
                <option value="PENDING">Pending</option>
                <option value="FAILED">Failed</option>
              </select>
              <ChevronDown
                size={14}
                className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-bold text-gray-400 dark:text-dark-muted uppercase tracking-wider ml-1">
              Month
            </label>
            <div className="relative">
              <select
                value={monthFilter}
                onChange={(e) => setMonthFilter(e.target.value)}
                className="w-full appearance-none pl-4 pr-10 py-2.5 rounded-lg bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-dark-border text-sm font-medium text-text dark:text-dark-text focus:ring-2 focus:ring-primary/20 outline-none cursor-pointer"
              >
                {getMonthOptions().map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <ChevronDown
                size={14}
                className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Payouts Table */}
      <div className="bg-white dark:bg-dark-surface rounded-xl shadow-sm border border-gray-200 dark:border-dark-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead className="text-xs text-gray-500 dark:text-dark-muted uppercase bg-gray-50 dark:bg-gray-900/50">
              <tr>
                <th className="px-6 py-4 font-bold tracking-wider">
                  Caregiver
                </th>
                <th className="px-6 py-4 font-bold tracking-wider">Month</th>
                <th className="px-6 py-4 font-bold tracking-wider">
                  Gross Amount
                </th>
                <th className="px-6 py-4 font-bold tracking-wider">
                  Platform Share (20% of net)
                </th>
                <th className="px-6 py-4 font-bold tracking-wider">
                  Caregiver Amount (80% of net)
                </th>
                <th className="px-6 py-4 font-bold tracking-wider">Status</th>
                <th className="px-6 py-4 font-bold tracking-wider">
                  Paid Date
                </th>
                <th className="px-6 py-4 font-bold tracking-wider text-center">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {loading ? (
                <tr>
                  <td colSpan={8} className="px-6 py-12 text-center">
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                    </div>
                  </td>
                </tr>
              ) : paginatedPayouts.length > 0 ? (
                paginatedPayouts.map((payout) => (
                  <tr
                    key={payout.id}
                    className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <User size={16} className="text-gray-400" />
                        <span className="font-medium text-text dark:text-dark-text">
                          {payout.caregiver?.name || "N/A"}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1">
                        <Calendar size={14} className="text-gray-400" />
                        <span className="text-gray-600 dark:text-dark-muted">
                          {payout.periodStart
                            ? new Date(payout.periodStart).toLocaleDateString(
                                "en-US",
                                {
                                  year: "numeric",
                                  month: "long",
                                },
                              )
                            : "N/A"}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="font-bold text-text dark:text-dark-text">
                        Br {payout.grossAmount?.toLocaleString() || "0"}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-gray-600 dark:text-dark-muted">
                        Br {Number(
                          payout.platformFee ?? payout.platformAmount ?? 0,
                        ).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="font-bold text-emerald-600 dark:text-emerald-400">
                        Br {Number(
                          payout.caregiverAmount ?? 0,
                        ).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${getStatusStyle(
                          payout.status,
                        )}`}
                      >
                        {payout.status}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-gray-600 dark:text-dark-muted">
                        {payout.settledAt
                          ? new Date(payout.settledAt).toLocaleDateString()
                          : "-"}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-center gap-2">
                        {payout.status === "FAILED" && (
                          <button
                            onClick={() => handleRetry(payout.id)}
                            className="p-2 rounded-lg bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
                            title="Retry Payout"
                          >
                            <RefreshCw size={16} />
                          </button>
                        )}
                        {payout.chapaReference && (
                          <span className="text-xs text-gray-500 dark:text-dark-muted">
                            Ref: {payout.chapaReference.slice(0, 8)}...
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={8}
                    className="px-6 py-12 text-center text-gray-500 dark:text-dark-muted"
                  >
                    No payouts found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {filteredPayouts.length > itemsPerPage && (
          <div className="border-t border-gray-200 dark:border-dark-border bg-gray-50/50 dark:bg-gray-900/50 p-2">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              itemsPerPage={itemsPerPage}
              onItemsPerPageChange={setItemsPerPage}
              totalItems={filteredPayouts.length}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default PayoutList;
